import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  FiMenu, FiX, FiChevronDown, FiGrid, 
  FiSearch, FiArrowRight, FiLayers, 
  FiCpu 
} from 'react-icons/fi';
import ThemeToggle from './ThemeToggle';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isBlogDropdownOpen, setIsBlogDropdownOpen] = useState(false);
  const location = useLocation();
  
  const logo = '/neovam_logo.png';
  const fallbackLogo = 'https://via.placeholder.com/40/38bdf8/ffffff?text=N';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'About', path: '/about' },
    { name: 'Careers', path: '/careers' },
    { name: 'Contact', path: '/contact' }
  ];

  // Blog dropdown menu items
  const blogMenuItems = [
    { 
      name: 'All Articles', 
      path: '/blog',
      description: 'Browse all our blog posts',
      icon: FiGrid
    },
    { 
      name: 'AI & Machine Learning', 
      path: '/blog/category/Artificial Intelligence',
      description: 'Latest AI trends and insights',
      icon: FiCpu
    },
    { 
      name: 'Cloud Computing', 
      path: '/blog/category/Cloud Computing',
      description: 'Cloud solutions and best practices',
      icon: FiLayers
    },
    { 
      name: 'Fintech Innovations', 
      path: '/blog/category/Fintech',
      description: 'Financial technology updates',
      icon: FiLayers
    },
    { 
      name: 'Digital Transformation', 
      path: '/blog/category/Digital Transformation',
      description: 'Business transformation stories',
      icon: FiGrid
    }
  ];

  // Popular tags for quick access
  const popularTags = [
    { name: 'AI', path: '/blog/tag/AI' },
    { name: 'Startup', path: '/blog/tag/Startup' },
    { name: 'Africa', path: '/blog/tag/Africa' },
    { name: 'Innovation', path: '/blog/tag/Innovation' },
    { name: 'Technology', path: '/blog/tag/Technology' }
  ];

  const isActive = (path) => location.pathname === path;
  const isBlogActive = () => location.pathname.startsWith('/blog');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isBlogDropdownOpen && !event.target.closest('.blog-dropdown')) {
        setIsBlogDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isBlogDropdownOpen]);

  return (
    <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 overflow-visible ${
      isScrolled 
        ? 'bg-white/90 dark:bg-dark-950/90 backdrop-blur-md shadow-lg border-b border-gray-200/20 dark:border-white/10' 
        : 'bg-transparent'
    }`}>
      <div className="w-full px-0 py-0 h-[88px] flex items-center">
        <div className="w-full flex items-center justify-between gap-4 px-4 sm:px-6 py-3 bg-white dark:bg-dark-800 border-y border-gray-100 dark:border-white/10 shadow-[0_14px_30px_rgba(60,49,100,0.10)] transition-all duration-300">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 group" aria-label="NeoVam Home">
            <div className="flex items-center gap-3">
              <div className="relative  w-[150px] p-1 flex items-center justify-center transition-all duration-300">
                <img
                  src={logo}
                  alt="NeoVam Logo"
                  className="h-full w-auto object-contain"
                  onError={(e) => {
                    e.currentTarget.src = fallbackLogo;
                  }}
                />
              </div>
            </div>
          </Link>

          {/* Desktop Navigation - Links */}
          <nav className="hidden lg:flex items-center gap-1 rounded-full px-2 py-1.5">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative px-4 py-2 text-[14px] font-semibold rounded-full transition-all duration-300 ${
                  isActive(item.path)
                    ? 'text-white bg-gradient-to-br from-primary-600 to-primary-700 shadow-md shadow-primary-500/25'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10'
                }`}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Blog Dropdown */}
            <div className="relative blog-dropdown">
              <button
                onClick={() => setIsBlogDropdownOpen(!isBlogDropdownOpen)}
                className={`relative flex items-center gap-1.5 px-4 py-2 text-[14px] font-semibold rounded-full transition-all duration-300 ${
                  isBlogActive()
                    ? 'text-white bg-gradient-to-br from-primary-600 to-primary-700 shadow-md shadow-primary-500/25'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10'
                }`}
              >
                <span>Blog</span>
                <FiChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${isBlogDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {isBlogDropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-80 bg-white dark:bg-dark-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-dark-600 overflow-hidden">
                  <div className="p-4 border-b border-gray-100 dark:border-dark-700">
                    <h3 className="font-bold text-gray-900 dark:text-white mb-2">Blog Categories</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Latest insights from NeoVam</p>
                  </div>
                  
                  <div className="p-4">
                    {blogMenuItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsBlogDropdownOpen(false)}
                        className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors mb-2 last:mb-0"
                      >
                        <div className="mt-0.5">
                          <item.icon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">{item.name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.description}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                  
                  <div className="p-4 border-t border-gray-100 dark:border-dark-700 bg-gray-50 dark:bg-dark-900/50">
                    <h4 className="font-bold text-gray-900 dark:text-white mb-2 text-sm">Popular Tags</h4>
                    <div className="flex flex-wrap gap-2">
                      {popularTags.map((tag) => (
                        <Link
                          key={tag.path}
                          to={tag.path}
                          onClick={() => setIsBlogDropdownOpen(false)}
                          className="px-3 py-1.5 bg-white dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-primary-50 hover:text-primary-700 dark:hover:bg-primary-900/30 dark:hover:text-primary-300 rounded-full text-xs font-medium transition-colors"
                        >
                          {tag.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </nav>

          {/* Actions */}
          <div className="hidden lg:flex items-center gap-3">
            <button className="w-[42px] h-[42px] rounded-full border border-gray-200 dark:border-white/15 bg-white dark:bg-dark-800 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 shadow-[0_8px_18px_rgba(18,25,42,0.07)] grid place-items-center transition-all duration-300" aria-label="Search">
              <FiSearch className="w-[18px] h-[18px]" />
            </button>
            <ThemeToggle />
            <Link
              to="/contact"
              className="flex items-center gap-2 px-5 py-3 bg-gradient-to-br from-primary-600 to-primary-700 text-white text-sm font-semibold rounded-full hover:shadow-glow hover:scale-105 transition-all duration-300"
            >
              Get Started
              <FiArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Mobile Controls */}
          <div className="lg:hidden flex items-center gap-2">
            <ThemeToggle />
            <button
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors touch-manipulation"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <FiX size={26} /> : <FiMenu size={26} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-4 right-4 mt-2 bg-white/95 dark:bg-dark-950/95 backdrop-blur-md border border-gray-200/60 dark:border-white/10 rounded-3xl py-4 shadow-2xl overflow-hidden">
            <nav className="flex flex-col space-y-1 px-2 sm:px-3">
              {navItems.map((item) => (
<Link
                      key={item.path}
                      to={item.path}
                      className={`block px-4 py-3 text-[15px] font-semibold rounded-2xl transition-all duration-300 touch-manipulation ${
                        isActive(item.path)
                          ? 'text-white bg-gradient-to-br from-primary-600 to-primary-700 shadow-md shadow-primary-500/25'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5'
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
              ))}
              
              {/* Mobile Blog Section */}
              <div className="mt-2 mb-2">
                <div className="px-4 py-2">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2">Blog</h3>
                </div>
                
                {blogMenuItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="flex items-center gap-3 px-4 py-3 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5 rounded-2xl transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <item.icon className="w-5 h-5" />
                    <div>
                      <div className="font-medium">{item.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-500">{item.description}</div>
                    </div>
                  </Link>
                ))}
                
                {/* Popular Tags in Mobile */}
                <div className="px-4 py-3">
                  <h4 className="font-bold text-gray-900 dark:text-white mb-2 text-sm">Popular Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {popularTags.map((tag) => (
                      <Link
                        key={tag.path}
                        to={tag.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="px-3 py-1.5 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-primary-100 hover:text-primary-700 dark:hover:bg-primary-900/30 dark:hover:text-primary-300 rounded-full text-xs font-medium transition-colors"
                      >
                        {tag.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
              
              <Link
                to="/contact"
                className="mt-2 px-4 py-4 bg-gradient-to-br from-primary-600 to-primary-700 text-white text-base font-semibold rounded-2xl text-center touch-manipulation"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Get Started
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
